"use client"

import { useState } from "react"
import { X, Upload, Loader, CheckCircle, AlertCircle, FileText } from "lucide-react"
import { postTeacherDigitiseNotes } from "../postFileAws"
import { postAws } from "../aws"

const UploadPdfForDigitisation = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    noteTitle: "",
    noteDescription: "",
    fileName: "",
  })
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState(null) // 'success', 'error', null
  const [statusMessage, setStatusMessage] = useState("")

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file && file.type === "application/pdf") {
      setSelectedFile(file)
      // Auto-generate filename from file name
      const originalName = file.name.replace(/\.pdf$/i, "").replace(/\s+/g, "-")
      setFormData((prev) => ({
        ...prev,
        fileName: `${originalName}.pdf`,
      }))
    } else {
      alert("Please select a valid PDF file")
      event.target.value = ""
    }
  }

  const handleInputChange = (field, value) => {
    if (field === "fileName") {
      // Remove spaces and ensure .pdf extension
      const cleanName = value.replace(/\s+/g, "-")
      const finalName = cleanName.endsWith(".pdf") ? cleanName : `${cleanName}.pdf`
      setFormData((prev) => ({ ...prev, [field]: finalName }))
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!selectedFile || !formData.noteTitle || !formData.fileName) {
      alert("Please fill in all required fields and select a PDF file")
      return
    }

    setIsUploading(true)
    setUploadStatus(null)

    try {
      // Get required data from localStorage
      const teacherId = localStorage.getItem("teacherId")
      const classroomId = localStorage.getItem("classRoomId")
      const organisationId = localStorage.getItem("organisationId")

      if (!teacherId || !classroomId) {
        throw new Error("Missing required user data. Please refresh and try again.")
      }

      // Upload PDF to S3
      const uploadResult = await postTeacherDigitiseNotes(
        selectedFile,
        teacherId,
        formData.fileName,
        formData.noteTitle,
      )

      if (!uploadResult || !uploadResult.uri) {
        throw new Error("Failed to upload PDF to storage")
      }

      // Prepare payload for digitization API
      const payload = {
        s3Uri: `s3://padhai-aurait-tech/${uploadResult.uri}`,
        teacherId,
        classroomId,
        noteTitle: formData.noteTitle,
        fileName: formData.fileName,
      }

      // Add organisationId if available
      if (organisationId) {
        payload.organisationId = organisationId
      }

      // Call digitization API
      const result = await postAws("https://api-dev.aurait.ai/DigitiseNotesFromPdf", "POST", payload)

      if (result.statusCode === 200) {
        setUploadStatus("success")
        setStatusMessage(
          "Digitisation process started! You will be able to see your digitised notes in notes page soon.",
        )

        // Switch to digitised notes tab and refresh the page
        setTimeout(() => {
          handleClose()
          // Trigger a callback to switch to digitised tab and refresh
          if (window.switchToDigitisedNotes) {
            window.switchToDigitisedNotes()
          }
        }, 2000)
      } else {
        throw new Error(result.message || "Failed to start digitization process")
      }
    } catch (error) {
      console.error("Error digitizing PDF:", error)
      setUploadStatus("error")
      setStatusMessage(error.message || "Failed to digitize PDF. Please try again.")
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    setFormData({ noteTitle: "", noteDescription: "", fileName: "" })
    setSelectedFile(null)
    setIsUploading(false)
    setUploadStatus(null)
    setStatusMessage("")
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800">Digitise Your PDF Notes</h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            disabled={isUploading}
          >
            <X size={20} />
          </button>
        </div>

        {uploadStatus === "success" && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
            <p className="text-green-700 text-sm">{statusMessage}</p>
          </div>
        )}

        {uploadStatus === "error" && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
            <p className="text-red-700 text-sm">{statusMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload PDF File <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                id="pdf-upload"
                disabled={isUploading}
              />
              <label htmlFor="pdf-upload" className="cursor-pointer flex flex-col items-center gap-2">
                {selectedFile ? (
                  <>
                    <FileText className="text-green-500" size={32} />
                    <p className="text-sm font-medium text-gray-700">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                  </>
                ) : (
                  <>
                    <Upload className="text-gray-400" size={32} />
                    <p className="text-sm font-medium text-gray-700">Click to upload PDF</p>
                    <p className="text-xs text-gray-500">Only PDF files are supported</p>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Note Title */}
          <div>
            <label htmlFor="noteTitle" className="block text-sm font-medium text-gray-700 mb-1">
              Note Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="noteTitle"
              required
              value={formData.noteTitle}
              onChange={(e) => handleInputChange("noteTitle", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter a title for your notes"
              disabled={isUploading}
            />
          </div>

          {/* Note Description */}
          <div>
            <label htmlFor="noteDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Note Description
            </label>
            <textarea
              id="noteDescription"
              value={formData.noteDescription}
              onChange={(e) => handleInputChange("noteDescription", e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Describe what these notes are about"
              disabled={isUploading}
            />
          </div>

          {/* File Name */}
          <div>
            <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
              File Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="fileName"
              required
              value={formData.fileName}
              onChange={(e) => handleInputChange("fileName", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="filename.pdf"
              disabled={isUploading}
            />
            <p className="text-xs text-gray-500 mt-1">Spaces will be replaced with hyphens. Must end with .pdf</p>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading || !selectedFile || !formData.noteTitle}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {isUploading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Digitise PDF
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UploadPdfForDigitisation
